import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription, distinctUntilChanged, filter, map, merge } from 'rxjs';
import { User } from '../entities/User';
import { AuthSessionService } from './auth/auth-session.service';
import { UserService } from './user.service';
import { AppStateService } from './app-state.service';

/** Projects the current authenticated user's entity and recovers missing profiles; authentication and session persistence remain in AuthSessionService. */
@Injectable({
  providedIn: 'root'
})
export class AppUserService implements OnDestroy {
  //#region State Management

  private readonly currentUserIdSubject = new BehaviorSubject<number | null>(null);
  /** Emits the current user id (null if logged out). */
  readonly currentUserId$ = this.currentUserIdSubject.asObservable();
  /** Gets the current user id (null if logged out). */
  get currentUserId(): number | null { return this.currentUserIdSubject.value; }

  private readonly currentUserSubject = new BehaviorSubject<User | null>(null);
  /** Emits the current user entity (null if logged out). */
  readonly currentUser$ = this.currentUserSubject.asObservable();
  /** Gets the current user entity (null if logged out). */
  get currentUser(): User | null { return this.currentUserSubject.value; }

  //#endregion
  //#region Properties

  private subscriptions: Subscription[] = [];
  private loadRevision = 0;
  private loadingRevision?: number;
  private destroyed = false;
  private retryTimer?: ReturnType<typeof setTimeout>;
  private retryDelayMs = 1000;

  //#endregion
  //#region Lifecycle

  constructor(
    private userService: UserService,
    private sessions: AuthSessionService,
    private appState: AppStateService
  ) {
    this.loadSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    clearTimeout(this.retryTimer);
    this.loadRevision++;
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  //#endregion
  //#region Initialization

  private async loadState(currentUserId: number): Promise<void> {
    const revision = ++this.loadRevision;
    this.loadingRevision = revision;
    clearTimeout(this.retryTimer);
    try {
      this.setCurrentUser(null);
      this.setCurrentUserId(currentUserId);
      if (currentUserId != null) {
        const currentUser = await this.getCurrentUserById(currentUserId);
        if (revision !== this.loadRevision || currentUserId !== this.currentUserId) {
          return;
        }
        this.setCurrentUser(currentUser);
        if (currentUser) { this.retryDelayMs = 1000; }
        else { this.scheduleRecovery(); }
      } else {
        this.setCurrentUser(null);
      }
      console.debug('AppUser.Init', { currentUserId: this.currentUserId });
    } catch (err) {
      console.error('AppUser.InitFailed', err);
      if (revision === this.loadRevision) {
        this.clearCurrentUser();
      }
    } finally {
      if (this.loadingRevision === revision) { this.loadingRevision = undefined; }
    }
  }

  private loadSubscriptions(): void {
    const sessionSubscription = this.sessions.identity$.subscribe(identity => {
      this.retryDelayMs = 1000;
      if (identity) { void this.loadState(identity.userId); }
      else { this.clearCurrentUser(); }
    });
    this.subscriptions.push(sessionSubscription);

    this.subscriptions.push(merge(
      this.appState.getAppState().pipe(filter(state => state.isActive)),
      this.appState.networkStatus$.pipe(map(status => status.currentStatus), distinctUntilChanged(), filter(connected => connected))
    ).subscribe(() => this.recoverMissingProfile()));

    const userUpdatedSubscription = this.userService.changeEmitted$.pipe(
      map(e => e.data),
      filter(u => u.Id === this.currentUserId)
    ).subscribe(user => {
      this.loadRevision++;
      clearTimeout(this.retryTimer);
      this.retryDelayMs = 1000;
      this.setCurrentUser(user);
      console.info('AppUser.UserUpdated', { user });
    });
    this.subscriptions.push(userUpdatedSubscription);
  }

  //#endregion
  //#region Private Methods

  private async getCurrentUserById(id: number): Promise<User | null> {
    try {
      return await this.userService.getUserByOnlineId(id);
    } catch (err) {
      console.warn('AppUser.GetUserFailed', { id, err });
      return null;
    }
  }

  private setCurrentUserId(id: number | null): void {
    if (this.currentUserId !== id) {
      this.currentUserIdSubject.next(id);
    }
  }

  private setCurrentUser(user: User | null): void {
    this.currentUserSubject.next(user);
  }

  private clearCurrentUser(): void {
    clearTimeout(this.retryTimer);
    this.loadRevision++;
    this.setCurrentUser(null);
    this.setCurrentUserId(null);
  }

  private recoverMissingProfile(): void {
    const identity = this.sessions.state.identity;
    if (this.destroyed || this.sessions.state.phase !== 'authenticated' || !identity || this.currentUser || this.loadingRevision === this.loadRevision) { return; }
    void this.loadState(identity.userId);
  }

  private scheduleRecovery(): void {
    clearTimeout(this.retryTimer);
    this.retryTimer = setTimeout(() => this.recoverMissingProfile(), this.retryDelayMs);
    this.retryDelayMs = Math.min(30000, this.retryDelayMs * 2);
  }


}